// Frontend/src/component/serviceprocess/ServiceProcess.jsx
import React, { useEffect, useRef } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import styles from "./ServiceProcess.module.css";

const steps = [
  {
    title: "AUDIT",
    icon: <SearchIcon />,
    color: "#00d4ff",
    gradient: "linear-gradient(135deg, #000, #fff)",
    description:
      "An AI agent that reviews voice calls, chats, and process logs to ensure compliance with SOPs and policies. It reconstructs workflows, detects deviations using GenAI reasoning, and produces evidence-backed audit scores and reports.",
  },
  {
    title: "INTEGRATION",
    icon: <SettingsIcon />,
    color: "#00d4ff",
    gradient: "linear-gradient(135deg, #000, #fff)",
    description:
      "An AI orchestration layer that connects voice bots, CRMs, ERPs, and other systems into a unified workflow. It uses LLM-driven mapping to translate data, understand intent, and trigger secure real-time actions.",
  },
  {
    title: "ANALYSIS",
    icon: <BarChartIcon />,
    color: "#00d4ff",
    gradient: "linear-gradient(135deg, #000, #fff)",
    description:
      "A structured approach to define the AI problem, assess data and system readiness, map current and future workflows, evaluate risk and ROI, and create a phased implementation plan.",
  },
  {
    title: "RESULTS",
    icon: <CheckCircleIcon />,
    color: "#00d4ff",
    gradient: "linear-gradient(135deg, #000, #fff)",
    description:
      "Delivers improved accuracy and consistency, reduced manual effort and costs, faster turnaround times, stronger compliance, and actionable insights for continuous improvement.",
  },
];


const ServiceProcess = () => {
  const titleRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.animate);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (titleRef.current) observer.observe(titleRef.current);
    stepRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="service" className={styles.section}>
      {/* Tech background elements */}
      <div className={styles.techBg}>
        <div className={styles.binaryGrid}></div>
        <div className={styles.glowOrb}></div>
        <div className={styles.circuitLines}></div>
      </div>

      <Box className={styles.container}>
        {/* Title */}
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={styles.titleWrapper}
        >
          <Typography className={styles.title}>
            Our Service Process That We Apply
          </Typography>
          <Typography className={styles.subtitle}>
            A streamlined workflow powered by advanced AI technology
          </Typography>
        </motion.div>

        {/* Steps */}
        <Grid
          container
          spacing={4}
          justifyContent="center"
          className={styles.gridWrapper}
          >
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={step.title}>
              <motion.div
                ref={(el) => (stepRefs.current[index] = el)}
                className={styles.stepBox}
                style={{ '--step-gradient': step.gradient }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                {/* Step number */}
                {/* <div className={styles.stepNumber}>
                  <span className={styles.numberText}>0{index + 1}</span>
                  <div className={styles.numberGlow}></div>
                </div> */}

                {/* Step connector (for desktop) */}
                <div className={styles.stepConnector}></div>

                {/* Icon container with glow */}
                <div className={styles.iconContainer}>
                  <div className={styles.iconGlow}></div>
                  {React.cloneElement(step.icon, {
                    className: styles.stepIcon,
                    style: { color: step.color }
                  })}
                </div>

                {/* Step content */}
                <Typography className={styles.stepTitle}>
                  {step.title}

                </Typography>

                <div className={styles.stepLine} style={{ background: step.gradient }} />
                <Typography className={styles.stepDescription}>
                  {step.description}
                </Typography>

                {/* Hover glow effect */}
                <div className={styles.stepGlow}></div>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Data flow animation */}
        {/* <div className={styles.dataFlow}></div> */}
      </Box>
    </section>
  );
};

export default ServiceProcess;